import type { Order } from '@/mocks/orders';

interface Props {
  order: Order;
}

const statusConfig: Record<Order['status'], { label: string; color: string; bg: string; icon: string }> = {
  confirmed: { label: 'Order Confirmed', color: '#1e5fa8', bg: '#e8f0fb', icon: 'ri-checkbox-circle-line' },
  production: { label: 'In Production', color: '#d97706', bg: '#fef3c7', icon: 'ri-hammer-line' },
  quality_check: { label: 'Quality Check', color: '#7c3aed', bg: '#ede9fe', icon: 'ri-shield-check-line' },
  shipped: { label: 'Shipped', color: '#059669', bg: '#d1fae5', icon: 'ri-ship-line' },
  arrived: { label: 'Arrived', color: '#0a2342', bg: '#e8f0fb', icon: 'ri-map-pin-2-line' },
};

export default function ShipmentDetails({ order }: Props) {
  const sc = statusConfig[order.status];

  return (
    <div className="space-y-5">
      {/* Order summary card */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-xs text-gray-400 font-semibold mb-1 uppercase tracking-widest">Order Number</div>
            <div className="text-xl font-black" style={{ color: '#0a2342' }}>{order.orderNumber}</div>
            <div className="text-sm text-gray-500 mt-0.5">{order.buyer} · {order.company} {order.flag}</div>
          </div>
          <span
            className="flex items-center gap-1.5 text-xs font-black px-3 py-1.5 rounded-full whitespace-nowrap flex-shrink-0"
            style={{ color: sc.color, backgroundColor: sc.bg }}
          >
            <i className={sc.icon} />{sc.label}
          </span>
        </div>

        {/* Order items */}
        <div className="space-y-2 mb-5">
          {order.items.map((item, i) => (
            <div key={i} className="flex items-start justify-between gap-4 py-3 border-t border-gray-100 first:border-0 first:pt-0">
              <div>
                <div className="text-sm font-bold text-gray-800">{item.product}</div>
                <div className="text-xs text-gray-500 mt-0.5">{item.spec}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-semibold text-gray-700">{item.quantity}</div>
                <div className="text-xs text-gray-400">{item.weight}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
          {[
            { label: 'Total Weight', val: order.totalWeight, icon: 'ri-scales-3-line' },
            { label: 'Total Value', val: order.totalValue, icon: 'ri-money-dollar-circle-line' },
            { label: 'Payment', val: order.paymentMethod.split('(')[0].trim(), icon: 'ri-bank-card-line' },
            { label: 'Placed', val: order.placedDate, icon: 'ri-calendar-line' },
          ].map((s) => (
            <div key={s.label}>
              <div className="text-xs text-gray-400 mb-0.5 flex items-center gap-1">
                <i className={`${s.icon} text-xs`} />{s.label}
              </div>
              <div className="text-sm font-bold text-gray-800">{s.val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Shipment info */}
      {order.shipment ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="font-black text-base mb-5 flex items-center gap-2" style={{ color: '#0a2342' }}>
            <i className="ri-ship-2-line" />Vessel & Shipment Info
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            {[
              { label: 'Vessel Name', val: order.shipment.vessel, icon: 'ri-ship-line' },
              { label: 'Voyage', val: order.shipment.voyage, icon: 'ri-route-line' },
              { label: 'Container No.', val: order.shipment.containerNo, icon: 'ri-box-3-line' },
              { label: 'B/L Number', val: order.shipment.blNumber, icon: 'ri-file-text-line' },
              { label: 'Port of Loading', val: order.shipment.portOfLoading, icon: 'ri-anchor-line' },
              { label: 'Port of Discharge', val: order.shipment.portOfDischarge, icon: 'ri-map-pin-2-line' },
              { label: 'ETD (Departure)', val: order.shipment.etd, icon: 'ri-calendar-event-line' },
              { label: 'ETA (Arrival)', val: order.shipment.eta, icon: 'ri-calendar-check-line' },
            ].map((row) => (
              <div key={row.label} className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5" style={{ backgroundColor: '#e8f0fb' }}>
                  <i className={`${row.icon} text-sm`} style={{ color: '#0a2342' }} />
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-medium">{row.label}</div>
                  <div className="text-sm font-bold text-gray-800 mt-0.5">{row.val}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-500">
            <i className="ri-information-line" style={{ color: '#0a2342' }} />
            Shipping line: <strong className="text-gray-700">{order.shipment.shippingLine}</strong>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="font-black text-base mb-3 flex items-center gap-2" style={{ color: '#0a2342' }}>
            <i className="ri-ship-2-line" />Vessel & Shipment Info
          </h3>
          <div className="flex items-center gap-3 py-4 text-gray-400">
            <i className="ri-time-line text-2xl" />
            <div>
              <div className="text-sm font-semibold text-gray-600">Not yet shipped</div>
              <div className="text-xs mt-0.5">Shipment details will appear here once your order is loaded and the B/L is issued.</div>
            </div>
          </div>
        </div>
      )}

      {/* Account manager */}
      <div className="rounded-2xl p-5 flex items-center gap-4" style={{ backgroundColor: '#0a2342' }}>
        <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-lg flex-shrink-0" style={{ backgroundColor: 'rgba(126,184,247,0.2)' }}>
          {order.accountManager.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white/60 text-xs font-semibold mb-0.5">Your Account Manager</div>
          <div className="text-white font-black text-sm">{order.accountManager}</div>
          <div className="text-white/50 text-xs mt-0.5">Available Mon–Fri 9AM–6PM CST</div>
        </div>
        <a
          href={`https://wa.me/${order.accountManagerPhone.replace(/\D/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-xs font-black px-4 py-2.5 rounded-xl cursor-pointer transition-colors whitespace-nowrap flex-shrink-0"
        >
          <i className="ri-whatsapp-line text-sm" />WhatsApp
        </a>
      </div>
    </div>
  );
}
